import React, { useCallback, useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import {
  archiveSponsoredVideo,
  createSponsoredVideo,
  getSponsoredVideo,
  listSponsoredVideos,
  publishSponsoredVideo,
  reorderSponsoredVideos,
  replaceSponsoredPromotions,
  setSponsoredVideoGlobal,
  updateSponsoredVideo,
  deleteSponsoredMedia,
  listSponsoredMedia,
  importSponsoredYoutubeVideo,
  uploadSponsoredMedia,
} from './sponsoredVideosApi';
import SponsoredTimelineEditor from './SponsoredTimelineEditor';
import { decodeYouTubeText, getYouTubeVideoId, youtubeThumbnail } from './sourcePickerUtils';
import './SponsoredVideos.css';

const EMPTY_FORM = {
  title: '',
  description: '',
  sourceType: 'youtube',
  sourceContentId: '',
  thumbnailUrl: '',
  durationSeconds: '',
  textSize: 24,
  qrSize: 140,
  widthPercent: 65,
  transparency: 80,
};

const errorMessage = (error) => error?.response?.data?.message || error?.message || 'Request failed';

export default function SponsoredVideos() {
  const [videos, setVideos] = useState([]);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState(EMPTY_FORM);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [youtubeQuery, setYoutubeQuery] = useState('');
  const [youtubeLength, setYoutubeLength] = useState('any');
  const [youtubeResults, setYoutubeResults] = useState([]);
  const [youtubeSearching, setYoutubeSearching] = useState(false);
  const [youtubeSelectingId, setYoutubeSelectingId] = useState('');
  const [media, setMedia] = useState([]);
  const [mediaLoading, setMediaLoading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(null);
  const [importing, setImporting] = useState(false);
  const [previewSourceUrl, setPreviewSourceUrl] = useState('');

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      setVideos(await listSponsoredVideos());
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const loadMedia = useCallback(async () => {
    setMediaLoading(true);
    try {
      setMedia(await listSponsoredMedia());
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setMediaLoading(false);
    }
  }, []);

  useEffect(() => { loadMedia(); }, [loadMedia]);

  const enabledVideos = useMemo(
    () => videos.filter((video) => video.globalFallbackEnabled),
    [videos]
  );

  const edit = (video) => {
    setSelected(video);
    setForm({
      title: video.title || '',
      description: video.description || '',
      sourceType: video.sourceType,
      sourceContentId: video.sourceContentId,
      thumbnailUrl: video.thumbnailUrl || '',
      durationSeconds: video.durationSeconds ?? '',
      textSize: video.textSize,
      qrSize: video.qrSize,
      widthPercent: video.widthPercent,
      transparency: video.transparency,
    });
    if (video.sourceType === 'youtube') setPreviewSourceUrl('');
    setError('');
  };

  useEffect(() => {
    if (form.sourceType !== 's3_video' || !form.sourceContentId) return;
    const match = media.find((item) => item.key === form.sourceContentId);
    if (match?.previewUrl) setPreviewSourceUrl(match.previewUrl);
  }, [form.sourceContentId, form.sourceType, media]);

  const selectVideo = async (video) => {
    edit(video);
    setBusy(true);
    try {
      edit(await getSponsoredVideo(video.id));
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const reset = () => {
    setSelected(null);
    setForm(EMPTY_FORM);
    setPreviewSourceUrl('');
    setError('');
  };

  const run = async (operation) => {
    setBusy(true);
    setError('');
    try {
      await operation();
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setBusy(false);
    }
  };

  const buildPayload = (overrides = {}) => ({
      ...form,
      durationSeconds: form.durationSeconds === '' ? null : Number(form.durationSeconds),
      textSize: Number(form.textSize),
      qrSize: Number(form.qrSize),
      widthPercent: Number(form.widthPercent),
      transparency: Number(form.transparency),
      ...overrides,
    });

  const save = (event) => {
    event.preventDefault();
    const payload = buildPayload();
    run(async () => {
      const saved = selected
        ? await updateSponsoredVideo(selected.id, payload)
        : await createSponsoredVideo(payload);
      edit(saved);
    });
  };

  const importAndSave = async () => {
    if (form.sourceType !== 'youtube' || !form.sourceContentId) return;
    setBusy(true);
    setImporting(true);
    setError('');
    try {
      const item = await importSponsoredYoutubeVideo({
        videoId: form.sourceContentId,
        title: form.title,
      });
      const payload = buildPayload({ sourceType: 's3_video', sourceContentId: item.key });
      const saved = selected
        ? await updateSponsoredVideo(selected.id, payload)
        : await createSponsoredVideo(payload);
      setMedia((current) => [item, ...current.filter((entry) => entry.key !== item.key)]);
      setPreviewSourceUrl(item.previewUrl || '');
      edit(saved);
      await load();
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setImporting(false);
      setBusy(false);
    }
  };

  const moveEnabled = (video, offset) => {
    const index = enabledVideos.findIndex((entry) => entry.id === video.id);
    const nextIndex = index + offset;
    if (index < 0 || nextIndex < 0 || nextIndex >= enabledVideos.length) return;
    const reordered = [...enabledVideos];
    [reordered[index], reordered[nextIndex]] = [reordered[nextIndex], reordered[index]];
    run(() => reorderSponsoredVideos(reordered.map((entry) => entry.id)));
  };

  const searchYouTube = async () => {
    const query = youtubeQuery.trim();
    if (!query) return;
    setYoutubeSearching(true);
    setError('');
    try {
      const params = {
        part: 'snippet',
        q: query,
        maxResults: 50,
        type: 'video',
        videoEmbeddable: 'true',
        videoSyndicated: 'true',
      };
      if (youtubeLength !== 'any') params.videoDuration = youtubeLength;
      const response = await axios.get('/api/youtube/search', {
        params,
        headers: { Authorization: `Bearer ${localStorage.getItem('token')}` },
      });
      setYoutubeResults(Array.isArray(response.data) ? response.data : response.data?.items || []);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setYoutubeSearching(false);
    }
  };

  const chooseYouTube = async (video) => {
    const videoId = getYouTubeVideoId(video);
    setForm((current) => ({
      ...current,
      sourceType: 'youtube',
      sourceContentId: videoId,
      title: decodeYouTubeText(video?.snippet?.title || current.title),
      description: decodeYouTubeText(video?.snippet?.description || current.description),
      thumbnailUrl: youtubeThumbnail(video),
      durationSeconds: '',
    }));
    setPreviewSourceUrl('');
    setYoutubeSelectingId(videoId);
    try {
      const response = await axios.post(
        '/api/youtube/check-videos',
        { videos: [{ content_id: videoId, title: video?.snippet?.title || '' }] },
        { headers: { Authorization: `Bearer ${localStorage.getItem('token')}` } }
      );
      const details = response.data?.results?.[0];
      if (!details?.playable) throw new Error(details?.reason || 'This YouTube video is unavailable');
      setForm((current) => current.sourceContentId === videoId
        ? { ...current, durationSeconds: details.durationSeconds || '' }
        : current);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setYoutubeSelectingId('');
    }
  };

  const chooseMedia = (item) => {
    setForm((current) => ({
      ...current,
      sourceType: 's3_video',
      sourceContentId: item.key,
      title: current.title || item.filename,
      thumbnailUrl: '',
      durationSeconds: '',
    }));
    setPreviewSourceUrl(item.previewUrl || '');
  };

  const handleUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;
    setUploadProgress(0);
    setError('');
    try {
      const item = await uploadSponsoredMedia(file, setUploadProgress);
      setMedia((current) => [item, ...current.filter((entry) => entry.key !== item.key)]);
      chooseMedia(item);
    } catch (requestError) {
      setError(errorMessage(requestError));
    } finally {
      setUploadProgress(null);
    }
  };

  const removeMedia = async (item) => {
    if (!window.confirm(`Delete ${item.filename} from sponsored media storage?`)) return;
    await run(async () => {
      await deleteSponsoredMedia(item.key);
      setMedia((current) => current.filter((entry) => entry.key !== item.key));
      if (form.sourceContentId === item.key) {
        setForm((current) => ({ ...current, sourceContentId: '' }));
        setPreviewSourceUrl('');
      }
    });
  };

  return (
    <section className="sponsored-videos" aria-labelledby="sponsored-videos-heading">
      <div className="sponsored-videos__header">
        <div>
          <h2 id="sponsored-videos-heading">Sponsored Videos</h2>
          <p>Manage globally available Free and Basic fallback sponsorship.</p>
        </div>
        <button type="button" onClick={reset}>New sponsored video</button>
      </div>

      {error && <div className="sponsored-videos__error" role="alert">{error}</div>}
      {loading ? <p>Loading sponsored videos…</p> : (
        <div className="sponsored-videos__layout">
          <div className="sponsored-videos__library">
            {videos.length === 0 && <p>No sponsored videos have been created.</p>}
            {videos.map((video) => (
              <article key={video.id} className={`sponsored-video-card ${selected?.id === video.id ? 'is-selected' : ''}`}>
                <button type="button" className="sponsored-video-card__main" onClick={() => selectVideo(video)}>
                  <strong>{video.title}</strong>
                  <span>{video.sourceType === 'youtube' ? 'YouTube' : 'S3 video'} · {video.promotionCount || video.promotions?.length || 0} promotions</span>
                  <span className={`sponsored-video-card__status status--${video.status}`}>{video.status}</span>
                </button>
                <div className="sponsored-video-card__actions">
                  <label>
                    <input
                      type="checkbox"
                      checked={video.globalFallbackEnabled}
                      disabled={busy || video.status !== 'published'}
                      onChange={(event) => run(() => setSponsoredVideoGlobal(video.id, event.target.checked))}
                    /> Global fallback
                  </label>
                  {video.globalFallbackEnabled && (
                    <span className="sponsored-video-card__order">
                      <button type="button" aria-label={`Move ${video.title} up`} disabled={busy || enabledVideos[0]?.id === video.id} onClick={() => moveEnabled(video, -1)}>↑</button>
                      <button type="button" aria-label={`Move ${video.title} down`} disabled={busy || enabledVideos[enabledVideos.length - 1]?.id === video.id} onClick={() => moveEnabled(video, 1)}>↓</button>
                    </span>
                  )}
                </div>
              </article>
            ))}
          </div>

          <form className="sponsored-video-form" onSubmit={save}>
            <h3>{selected ? `Edit ${selected.title}` : 'Create sponsored video'}</h3>
            <label>Source<select value={form.sourceType} onChange={(event) => { setForm({ ...form, sourceType: event.target.value, sourceContentId: '' }); setPreviewSourceUrl(''); }}><option value="youtube">YouTube video</option><option value="s3_video">Uploaded sponsored video</option></select></label>
            {form.sourceType === 'youtube' ? (
              <div className="sponsored-source-picker">
                <div className="sponsored-source-picker__toolbar">
                  <input value={youtubeQuery} placeholder="Search YouTube videos" onChange={(event) => setYoutubeQuery(event.target.value)} onKeyDown={(event) => { if (event.key === 'Enter') { event.preventDefault(); searchYouTube(); } }} />
                  <select aria-label="YouTube video duration" value={youtubeLength} onChange={(event) => setYoutubeLength(event.target.value)}>
                    <option value="any">Any duration</option><option value="short">Short (&lt;4 min)</option><option value="medium">Medium (4–20 min)</option><option value="long">Long (&gt;20 min)</option>
                  </select>
                  <button type="button" disabled={youtubeSearching || !youtubeQuery.trim()} onClick={searchYouTube}>{youtubeSearching ? 'Searching…' : 'Search'}</button>
                </div>
                <div className="sponsored-source-picker__results">
                  {youtubeResults.map((video) => {
                    const videoId = getYouTubeVideoId(video);
                    return <button type="button" key={videoId} disabled={youtubeSelectingId === videoId} className={form.sourceContentId === videoId ? 'is-selected' : ''} onClick={() => chooseYouTube(video)}><img src={youtubeThumbnail(video)} alt="" /><span><strong>{decodeYouTubeText(video?.snippet?.title)}</strong><small>{youtubeSelectingId === videoId ? 'Loading duration…' : decodeYouTubeText(video?.snippet?.channelTitle || 'YouTube')}</small></span></button>;
                  })}
                </div>
              </div>
            ) : (
              <div className="sponsored-source-picker">
                <div className="sponsored-source-picker__upload">
                  <label className="sponsored-upload-button">Upload sponsored video<input type="file" accept="video/mp4,video/quicktime,video/webm,video/x-m4v" disabled={uploadProgress !== null} onChange={handleUpload} /></label>
                  {uploadProgress !== null && <span>Uploading… {uploadProgress}%</span>}
                </div>
                {mediaLoading ? <p>Loading sponsored media…</p> : <div className="sponsored-source-picker__results sponsored-source-picker__results--media">{media.map((item) => <div className={`sponsored-media-item ${form.sourceContentId === item.key ? 'is-selected' : ''}`} key={item.key}><button type="button" onClick={() => chooseMedia(item)}><video src={item.previewUrl} muted preload="metadata" /><span><strong>{item.filename}</strong><small>{(item.size / 1024 / 1024).toFixed(1)} MB</small></span></button><button type="button" className="danger" aria-label={`Delete ${item.filename}`} onClick={() => removeMedia(item)}>Delete</button></div>)}</div>}
              </div>
            )}
            <label>Title<input required maxLength="255" value={form.title} onChange={(event) => setForm({ ...form, title: event.target.value })} /></label>
            <label>Description<textarea maxLength="5000" value={form.description} onChange={(event) => setForm({ ...form, description: event.target.value })} /></label>
            <label>{form.sourceType === 'youtube' ? 'YouTube video ID' : 'Existing S3 asset key or URL'}<input required value={form.sourceContentId} onChange={(event) => setForm({ ...form, sourceContentId: event.target.value })} /></label>
            <label>Thumbnail URL<input value={form.thumbnailUrl} onChange={(event) => setForm({ ...form, thumbnailUrl: event.target.value })} /></label>
            <label>Duration in minutes<input type="number" min="0.001" step="0.001" value={form.durationSeconds === '' ? '' : Number((Number(form.durationSeconds) / 60).toFixed(3))} onChange={(event) => setForm({ ...form, durationSeconds: event.target.value === '' ? '' : Number(event.target.value) * 60 })} /></label>
            <div className="sponsored-video-form__styles">
              <label>Text size<input type="number" min="12" max="64" value={form.textSize} onChange={(event) => setForm({ ...form, textSize: event.target.value })} /></label>
              <label>QR size<input type="number" min="64" max="320" value={form.qrSize} onChange={(event) => setForm({ ...form, qrSize: event.target.value })} /></label>
              <label>Width (%)<input type="number" min="40" max="100" value={form.widthPercent} onChange={(event) => setForm({ ...form, widthPercent: event.target.value })} /></label>
              <label>Transparency<input type="number" min="0" max="95" value={form.transparency} onChange={(event) => setForm({ ...form, transparency: event.target.value })} /></label>
            </div>
            <div className="sponsored-video-form__actions">
              <button type="submit" disabled={busy}>{busy ? 'Saving…' : 'Save'}</button>
              {form.sourceType === 'youtube' && <button type="button" className="import" disabled={busy || !form.sourceContentId || !form.title} onClick={importAndSave}>{importing ? 'Importing to S3…' : 'Import'}</button>}
              {selected && selected.status !== 'archived' && <button type="button" disabled={busy || (selected.status !== 'published' && !(selected.promotionCount || selected.promotions?.length))} onClick={() => run(() => publishSponsoredVideo(selected.id, selected.status !== 'published'))}>{selected.status === 'published' ? 'Unpublish' : 'Publish'}</button>}
              {selected && selected.status !== 'archived' && <button type="button" className="danger" disabled={busy} onClick={() => run(async () => { await archiveSponsoredVideo(selected.id); reset(); })}>Archive</button>}
            </div>
            {selected && !(selected.promotionCount || selected.promotions?.length) && <p className="sponsored-video-form__note">Add at least one promotion in the timeline editor before publishing.</p>}
          </form>
          {selected && selected.status !== 'archived' && selected.promotions && (
            <SponsoredTimelineEditor
              video={{
                ...selected,
                previewSourceUrl,
                textSize: Number(form.textSize),
                qrSize: Number(form.qrSize),
                widthPercent: Number(form.widthPercent),
                transparency: Number(form.transparency),
              }}
              busy={busy}
              onDuration={(duration) => setForm((current) => ({ ...current, durationSeconds: Number(duration.toFixed(3)) }))}
              onSave={(promotions) => run(async () => edit(await replaceSponsoredPromotions(selected.id, promotions)))}
            />
          )}
        </div>
      )}
    </section>
  );
}
